import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    CreateAxiosDefaults,
    InternalAxiosRequestConfig,
} from 'axios';

export interface RequestConfig extends AxiosRequestConfig {
    urlParams?: {
        [key: string]: string | number;
    };
}

export abstract class BaseClient {
    private readonly server: AxiosInstance;

    protected constructor(baseUrl: string, headers: CreateAxiosDefaults['headers'] = {}) {
        this.server = axios.create({
            baseURL: baseUrl,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...headers,
            },
            withCredentials: true,
        });

        // path params replace interceptor
        this.server.interceptors.request.use(
            (config: RequestConfig & Pick<InternalAxiosRequestConfig, 'headers'>) => {
                if (!config.url || !config.urlParams) {
                    return config;
                }

                const urlParams = config.urlParams || {};
                const resolvedConfigUrl = config.url
                    .split('/')
                    .map((el) => {
                        const replacement = urlParams[el.slice(1)];
                        if (el.startsWith(':') && replacement) {
                            return encodeURIComponent(replacement);
                        }
                        return el;
                    })
                    .join('/');

                return {
                    ...config,
                    url: resolvedConfigUrl,
                };
            },
        );
    }

    protected async sendRequest(requestConfig: RequestConfig): Promise<AxiosResponse> {
        try {
            return await this.server.request(requestConfig);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                throw error.response;
            } else if (axios.isAxiosError(error) && error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                throw new Error('No server response for request: ' + JSON.stringify(requestConfig));
            }
            // Something happened in setting up the request that triggered an Error
            throw new Error(
                'Request failed for unknown reason. Possibly frontend bug while constructing request. Request: ' +
                    JSON.stringify(requestConfig),
            );
        }
    }
}
